import { Sparkles, Wand2 } from "lucide-react";
import { TIER_META, PERIODS, RUBRIC_TYPES, gradeValue, ruleText, isNumeric } from "@/lib/rubric";

export function TierBadge({ tier, label, size = "text-[10px]", testid }) {
  if (!tier) return null;
  const m = TIER_META[tier] || TIER_META.full;
  return <span data-testid={testid} className={`${size} font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${m.cls}`}>{label || m.label}</span>;
}

export function RubricSummary({ rubric, compact, testid }) {
  if (!rubric || rubric.type === "yesno") return compact ? null : <span className="text-xs text-stone-500" data-testid={testid}>Done / not done</span>;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={testid}>
      {rubric.tiers.map((t) => (
        <span key={t.key} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${TIER_META[t.key].cls}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${TIER_META[t.key].dot}`} />
          <span className="font-semibold">{t.label}</span>
          <span className="opacity-70">· {ruleText(rubric.type, t.rule, rubric.unit)} · {Math.round(t.weight * 100)}%</span>
        </span>
      ))}
    </div>
  );
}

export function TierInput({ rubric, value, onChange, dark, idPrefix }) {
  if (!rubric || rubric.type === "yesno") return null;
  const base = dark
    ? "bg-white/10 text-stone-100 border-white/20 placeholder:text-stone-500 focus:border-amber-300"
    : "bg-white text-stone-900 border-stone-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200";
  const preview = value !== "" && value != null ? gradeValue(rubric, isNumeric(rubric.type) ? Number(value) : value) : null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${idPrefix}-tier-input`}>
      {rubric.type === "time_of_day" ? (
        <div className="flex flex-wrap gap-1.5">
          {PERIODS.map((p) => (
            <button type="button" key={p.v} data-testid={`${idPrefix}-period-${p.v}`} onClick={() => onChange(p.v)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${value === p.v ? "bg-amber-500 text-stone-900" : dark ? "bg-white/10 text-stone-200" : "bg-stone-100 text-stone-700"}`}>
              {p.label}
            </button>
          ))}
        </div>
      ) : rubric.type === "time_by" ? (
        <input type="time" data-testid={`${idPrefix}-time-input`} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          className={`px-3 py-1.5 rounded-xl border text-sm outline-none ${base}`} />
      ) : (
        <label className="inline-flex items-center gap-1.5 text-sm">
          <input type="number" min="0" step="any" data-testid={`${idPrefix}-number-input`} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
            placeholder={rubric.type === "limit" ? "how many" : "how much"} className={`w-24 px-3 py-1.5 rounded-xl border text-sm outline-none ${base}`} />
          {rubric.unit && <span className={dark ? "text-stone-400" : "text-stone-500"}>{rubric.unit}</span>}
        </label>
      )}
      {preview && <TierBadge tier={preview.key} label={`→ ${preview.label}`} testid={`${idPrefix}-tier-preview`} />}
    </div>
  );
}

export function QualityMix({ mix, testid }) {
  if (!mix || !mix.total) return null;
  const delta = mix.full_rate != null && mix.prev_full_rate != null ? Math.round((mix.full_rate - mix.prev_full_rate) * 100) : null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-stone-600" data-testid={testid}>
      <span className="font-bold uppercase tracking-widest text-[10px] text-stone-500">Last {mix.days} days</span>
      {TIER_ORDER.filter((k) => mix[k] > 0).map((k) => (
        <span key={k} className={`px-2 py-0.5 rounded-full ${TIER_META[k].cls}`}>{TIER_META[k].label} {mix[k]}</span>
      ))}
      {mix.full_rate != null && <span className="font-mono">Full-rate {Math.round(mix.full_rate * 100)}%</span>}
      {delta != null && delta !== 0 && <span className={`font-semibold ${delta > 0 ? "text-emerald-700" : "text-stone-500"}`}>{delta > 0 ? "↑" : "↓"} {Math.abs(delta)} pts vs before</span>}
    </div>
  );
}

export function WeekdayInsights({ insights, rubric, testid, compact }) {
  if (!insights || !insights.total) return null;
  const max = Math.max(1, ...insights.days.map((d) => d.total));
  const h = compact ? 28 : 40;
  return (
    <div className={`mt-2 ${compact ? "" : "rounded-2xl bg-stone-50 border border-stone-200 p-3"}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500">By weekday · {insights.weeks} weeks</span>
        {insights.time?.median && (
          <span className="text-[11px] text-stone-600" data-testid={`${testid}-time`}>
            {rubric?.type === "time_by" ? `Median ${insights.time.median}` : `Mostly ${insights.time.top_period}`}
            {insights.time.earliest_day ? ` · earliest on ${insights.time.earliest_day}s (${insights.time.earliest_median})` : ""}
          </span>
        )}
      </div>
      <div className="grid grid-cols-7 gap-1.5 mt-2">
        {insights.days.map((d) => {
          const strong = d.day === insights.best_day, stretch = d.day === insights.hardest_day;
          return (
            <div key={d.day} className="flex flex-col items-center gap-1" data-testid={`${testid}-${d.day}`} title={`${d.day}: ${d.full} full · ${d.partial} partial · ${d.showed_up} showed up`}>
              <div className="w-full flex flex-col-reverse rounded-md overflow-hidden bg-stone-200/60" style={{ height: h }}>
                {TIER_ORDER.map((k) => d[k] > 0 && <div key={k} className={TIER_META[k].dot} style={{ height: `${(d[k] / max) * 100}%` }} />)}
              </div>
              <span className={`text-[10px] font-semibold ${strong ? "text-emerald-700" : stretch ? "text-amber-700" : "text-stone-500"}`}>{d.day}</span>
            </div>
          );
        })}
      </div>
      {insights.takeaway && <p className="text-xs text-stone-600 mt-2" data-testid={`${testid}-takeaway`}>{insights.takeaway}</p>}
    </div>
  );
}

function TierRow({ type, unit, tier, onChange, onRemove, idPrefix }) {
  const setRule = (patch) => onChange({ ...tier, rule: { ...tier.rule, ...patch } });
  const inputCls = "px-2 py-1 rounded-lg border border-stone-300 text-xs w-24 outline-none focus:border-amber-600";
  const catchAll = tier.key === "showed_up" || (type === "time_of_day" && tier.key === "partial" && !(tier.rule.periods || []).length);
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 border-b border-stone-100 last:border-0" data-testid={`${idPrefix}-tier-row-${tier.key}`}>
      <TierBadge tier={tier.key} />
      <input value={tier.label} onChange={(e) => onChange({ ...tier, label: e.target.value })} maxLength={40} data-testid={`${idPrefix}-tier-label-${tier.key}`}
        className="px-2 py-1 rounded-lg border border-stone-300 text-xs w-36 outline-none focus:border-amber-600" placeholder="Label" />
      {catchAll ? (
        <span className="text-xs text-stone-500">anything else</span>
      ) : type === "time_by" ? (
        <label className="text-xs text-stone-600 inline-flex items-center gap-1">by <input type="time" value={tier.rule.max_time || ""} onChange={(e) => setRule({ max_time: e.target.value })} data-testid={`${idPrefix}-tier-time-${tier.key}`} className={inputCls} /></label>
      ) : type === "time_of_day" ? (
        <div className="flex gap-1">
          {PERIODS.map((p) => {
            const on = (tier.rule.periods || []).includes(p.v);
            return (
              <button type="button" key={p.v} data-testid={`${idPrefix}-tier-period-${tier.key}-${p.v}`}
                onClick={() => setRule({ periods: on ? tier.rule.periods.filter((x) => x !== p.v) : [...(tier.rule.periods || []), p.v] })}
                className={`text-[11px] px-2 py-0.5 rounded-full ${on ? "bg-amber-500 text-stone-900" : "bg-stone-100 text-stone-600"}`}>{p.label}</button>
            );
          })}
        </div>
      ) : type === "limit" ? (
        <label className="text-xs text-stone-600 inline-flex items-center gap-1">at most <input type="number" min="0" value={tier.rule.max ?? ""} onChange={(e) => setRule({ max: Number(e.target.value) })} data-testid={`${idPrefix}-tier-max-${tier.key}`} className={inputCls} /> {unit}</label>
      ) : (
        <label className="text-xs text-stone-600 inline-flex items-center gap-1">at least <input type="number" min="0" value={tier.rule.min ?? ""} onChange={(e) => setRule({ min: Number(e.target.value) })} data-testid={`${idPrefix}-tier-min-${tier.key}`} className={inputCls} /> {unit}</label>
      )}
      {tier.key !== "full" && (
        <label className="text-xs text-stone-600 inline-flex items-center gap-1">worth <input type="number" min="10" max="90" step="10" value={Math.round(tier.weight * 100)}
          onChange={(e) => onChange({ ...tier, weight: Math.min(0.9, Math.max(0.1, Number(e.target.value) / 100)) })} data-testid={`${idPrefix}-tier-weight-${tier.key}`} className="px-2 py-1 rounded-lg border border-stone-300 text-xs w-16 outline-none" />%</label>
      )}
      {tier.key !== "full" && <button type="button" onClick={onRemove} data-testid={`${idPrefix}-tier-remove-${tier.key}`} className="text-[11px] text-stone-400 hover:text-red-700 ml-auto">remove</button>}
    </div>
  );
}

const NEW_TIER = { partial: { key: "partial", label: "Partial", rule: {}, weight: 0.6 }, small: { key: "small", label: "Small step", rule: {}, weight: 0.3 }, showed_up: { key: "showed_up", label: "Noticed", rule: {}, weight: 0.1 } };
const TIER_ORDER = ["full", "partial", "small", "showed_up"];

export function RubricEditor({ value, onChange, onSuggest, suggesting, why, idPrefix = "rubric", showCountsFrom }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const setType = (type) => {
    const tiers = type === "yesno" ? [{ key: "full", label: "Done", rule: {}, weight: 1 }] : [{ key: "full", label: "Full", rule: {}, weight: 1 }, NEW_TIER.partial];
    set({ type, tiers, unit: type === "duration" ? "min" : "" });
  };
  const missing = ["partial", "small", "showed_up"].filter((k) => !value.tiers.some((t) => t.key === k));
  const addTier = (k) => set({ tiers: [...value.tiers, NEW_TIER[k]].sort((a, b) => TIER_ORDER.indexOf(a.key) - TIER_ORDER.indexOf(b.key)) });
  return (
    <div className="space-y-3" data-testid={`${idPrefix}-editor`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-bold uppercase tracking-widest text-stone-600">How is success measured?</div>
        {onSuggest && (
          <button type="button" onClick={onSuggest} disabled={suggesting} data-testid={`${idPrefix}-suggest-btn`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-amber-700 disabled:opacity-50">
            <Wand2 className="w-3.5 h-3.5" /> {suggesting ? "Thinking…" : "Suggest with AI"}
          </button>
        )}
      </div>
      {why && <p className="text-xs text-amber-800 italic inline-flex items-start gap-1" data-testid={`${idPrefix}-why`}><Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {why}</p>}
      <div className="flex flex-wrap gap-2">
        {RUBRIC_TYPES.map((t) => (
          <button type="button" key={t.v} data-testid={`${idPrefix}-type-${t.v}`} onClick={() => setType(t.v)} title={t.hint}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${value.type === t.v ? "bg-amber-600 text-white" : "bg-stone-100 text-stone-700"}`}>{t.label}</button>
        ))}
      </div>
      <p className="text-xs text-stone-500">{RUBRIC_TYPES.find((t) => t.v === value.type)?.hint}</p>
      {value.type !== "yesno" && (
        <div className="rounded-2xl border border-stone-200 px-3">
          {isNumeric(value.type) && (
            <label className="flex items-center gap-2 text-xs text-stone-600 pt-2">Unit <input value={value.unit || ""} onChange={(e) => set({ unit: e.target.value })} maxLength={20} data-testid={`${idPrefix}-unit`} placeholder="min, pages, glasses…" className="px-2 py-1 rounded-lg border border-stone-300 text-xs w-32 outline-none focus:border-amber-600" /></label>
          )}
          {value.tiers.map((t) => (
            <TierRow key={t.key} type={value.type} unit={value.unit} tier={t} idPrefix={idPrefix}
              onChange={(nt) => set({ tiers: value.tiers.map((x) => (x.key === t.key ? nt : x)) })}
              onRemove={() => set({ tiers: value.tiers.filter((x) => x.key !== t.key) })} />
          ))}
          {missing.length > 0 && (
            <div className="flex gap-2 py-2">
              {missing.map((k) => <button type="button" key={k} onClick={() => addTier(k)} data-testid={`${idPrefix}-add-tier-${k}`} className="text-xs font-semibold text-amber-800 hover:text-amber-900">+ add {TIER_META[k].label} tier</button>)}
            </div>
          )}
        </div>
      )}
      {showCountsFrom && value.type !== "yesno" && (
        <label className="flex items-center gap-2 text-xs text-stone-600">Counts toward the weekly goal from
          <select value={value.counts_from || "showed_up"} onChange={(e) => set({ counts_from: e.target.value })} data-testid={`${idPrefix}-counts-from`} className="px-2 py-1 rounded-lg border border-stone-300 text-xs">
            <option value="showed_up">any tier</option><option value="partial">Partial and up</option><option value="full">Full only</option>
          </select>
        </label>
      )}
    </div>
  );
}
